import React, { useEffect } from 'react'
import { AlertTriangleIcon, AlertCircleIcon, ArrowRightIcon } from 'lucide-react'
import { ConsistencyIssue, IssueTarget } from '../../types/agreement'
import { ensureScrollFlashCSS, scrollAndFlash } from '../../utils/scrollAndFlash'

interface Props {
  issues: ConsistencyIssue[]
  onNavigate?: (target: IssueTarget) => void
  /**
   * If true, the panel collapses itself when there are no issues so it
   * does not take any vertical space.
   */
  hideWhenEmpty?: boolean
  /**
   * Optional title override.
   */
  title?: string
  /**
   * When set, only issues whose target.tab matches `currentTab` are listed in
   * the main panel; issues for other tabs are summarized in a footer link so
   * the user can jump to them without being overwhelmed on the current tab.
   * Pass `null`/undefined to show all issues regardless of tab.
   */
  currentTab?: string | null
}

export default function ConsistencyWarnings({
  issues,
  onNavigate,
  hideWhenEmpty = true,
  title,
  currentTab,
}: Props) {
  useEffect(() => {
    ensureScrollFlashCSS()
  }, [])

  if (!issues || issues.length === 0) {
    if (hideWhenEmpty) return null
    return (
      <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
        No consistency issues.
      </div>
    )
  }

  // Tab-aware partitioning: when `currentTab` is provided, issues whose target
  // tab does not match are pushed to an "other tabs" summary instead of cluttering
  // the panel for the current view.
  const isOnCurrentTab = (issue: ConsistencyIssue): boolean => {
    if (!currentTab) return true
    const t = issue.target?.tab
    // If no tab metadata, treat as global (always show).
    if (!t) return true
    return t === currentTab
  }

  const visible = currentTab ? issues.filter(isOnCurrentTab) : issues
  const otherTabIssues = currentTab ? issues.filter((i) => !isOnCurrentTab(i)) : []

  const blockers = visible.filter((i) => i.level === 'block')
  const warnings = visible.filter((i) => i.level === 'warn')
  const otherBlockers = otherTabIssues.filter((i) => i.level === 'block').length
  const otherWarnings = otherTabIssues.filter((i) => i.level === 'warn').length

  // If nothing on this tab AND nothing on other tabs, collapse.
  if (blockers.length === 0 && warnings.length === 0 && otherTabIssues.length === 0) {
    if (hideWhenEmpty) return null
  }

  const handleClick = (issue: ConsistencyIssue) => {
    if (onNavigate) onNavigate(issue.target)
    if (issue.target?.anchor) scrollAndFlash(issue.target.anchor, issue.level)
  }

  // Render a footer summarizing issues that exist on other tabs. Clicking the
  // summary jumps to the first such issue.
  const renderOtherTabsFooter = () => {
    if (otherTabIssues.length === 0) return null
    const firstOther = otherTabIssues[0]
    const parts: string[] = []
    if (otherBlockers > 0) parts.push(`${otherBlockers} blocker${otherBlockers === 1 ? '' : 's'}`)
    if (otherWarnings > 0) parts.push(`${otherWarnings} warning${otherWarnings === 1 ? '' : 's'}`)
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 flex items-center justify-between">
        <span>{parts.join(' and ')} on other tabs.</span>
        <button
          type="button"
          onClick={() => handleClick(firstOther)}
          className="inline-flex shrink-0 items-center gap-1 rounded border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
        >
          Go to first
          <ArrowRightIcon className="h-3 w-3" />
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {blockers.length > 0 && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-red-800">
            <AlertCircleIcon className="h-4 w-4" />
            {title || 'Issues that prevent saving'}
          </div>
          <ul className="space-y-1.5">
            {blockers.map((issue, idx) => (
              <li
                key={`${issue.code}-${idx}`}
                className="flex items-start justify-between gap-3 text-sm text-red-900"
              >
                <span className="flex-1">{issue.message}</span>
                <button
                  type="button"
                  onClick={() => handleClick(issue)}
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-red-300 bg-white px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-100"
                >
                  Fix this
                  <ArrowRightIcon className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-yellow-800">
            <AlertTriangleIcon className="h-4 w-4" />
            {title ? `${title} (warnings)` : 'Warnings'}
          </div>
          <ul className="space-y-1.5">
            {warnings.map((issue, idx) => (
              <li
                key={`${issue.code}-${idx}`}
                className="flex items-start justify-between gap-3 text-sm text-yellow-900"
              >
                <span className="flex-1">{issue.message}</span>
                <button
                  type="button"
                  onClick={() => handleClick(issue)}
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-yellow-300 bg-white px-2 py-0.5 text-xs font-medium text-yellow-800 hover:bg-yellow-100"
                >
                  Fix this
                  <ArrowRightIcon className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {renderOtherTabsFooter()}
    </div>
  )
}
